import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import SearchableSelect from '../components/SearchableSelect';
import DateTimeFields from '../components/DateTimeFields';
import ScopeChecklist from '../components/ScopeChecklist';
import { btnPrimary, cardClass } from '../components/PageHeader';
import { ACCOUNT_TYPE_LABELS } from '../utils/statusConstants';
import {
  ACCOUNT_TYPE_FOR_CATEGORY,
  getScopeItems,
  getServiceTypesForCategory,
  propertyCategoryOptions,
  serviceTypeOptionsForCategory,
} from '../utils/cleaningCatalog';

const CreateJob = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    property_category: '',
    service_type: '',
    scope_items: [],
    account_id: '',
    service_location_id: '',
    scheduled_start: '',
    notes: '',
    status: 'requested',
  });

  useEffect(() => {
    apiFetch('/admin/accounts').then((r) => setAccounts(r.data || []));
  }, []);

  useEffect(() => {
    if (!form.account_id) {
      setLocations([]);
      return;
    }
    apiFetch(`/admin/locations?account_id=${form.account_id}`).then((r) =>
      setLocations(r.data || [])
    );
  }, [form.account_id]);

  const filteredAccounts = useMemo(() => {
    if (!form.property_category) return accounts;
    const accountType = ACCOUNT_TYPE_FOR_CATEGORY[form.property_category];
    return accounts.filter((a) => a.account_type === accountType);
  }, [accounts, form.property_category]);

  const accountOptions = useMemo(
    () =>
      filteredAccounts.map((a) => ({
        value: a.id,
        label: `${a.name} (${ACCOUNT_TYPE_LABELS[a.account_type] || a.account_type})`,
      })),
    [filteredAccounts]
  );

  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        value: l.id,
        label: `${l.label || l.address_line1} — ${l.city}`,
      })),
    [locations]
  );

  const serviceTypeOptions = useMemo(
    () => (form.property_category ? serviceTypeOptionsForCategory(form.property_category) : []),
    [form.property_category]
  );

  const scopeItems = useMemo(
    () =>
      form.property_category && form.service_type
        ? getScopeItems(form.property_category, form.service_type)
        : [],
    [form.property_category, form.service_type]
  );

  const setPropertyCategory = (property_category) => {
    const types = getServiceTypesForCategory(property_category);
    setForm({
      ...form,
      property_category,
      service_type: types[0] || '',
      scope_items: [],
      account_id: '',
      service_location_id: '',
    });
  };

  const setServiceType = (service_type) => {
    setForm({
      ...form,
      service_type,
      scope_items: [],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.scope_items.length === 0) {
      toast.error('Select at least one item in the scope checklist');
      return;
    }
    try {
      const body = {
        ...form,
        scheduled_start: form.scheduled_start || null,
      };
      const res = await apiFetch('/admin/jobs', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      toast.success('Job created');
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create job');
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold mb-6">New job</h1>
      <form onSubmit={submit} className="space-y-6">
        <section className={`${cardClass} p-6 space-y-4`}>
          <h2 className="font-medium text-slate-900">1. Property & cleaning type</h2>
          <SearchableSelect
            label="Property type"
            value={form.property_category}
            onChange={setPropertyCategory}
            options={propertyCategoryOptions}
            placeholder="Residential or commercial…"
            required
          />
          <SearchableSelect
            label="Cleaning type"
            value={form.service_type}
            onChange={setServiceType}
            options={serviceTypeOptions}
            placeholder={
              form.property_category
                ? 'Select cleaning type…'
                : 'Choose property type first'
            }
            disabled={!form.property_category}
            required
          />
        </section>

        <section className={`${cardClass} p-6`}>
          <h2 className="font-medium text-slate-900 mb-4">2. Scope of work</h2>
          <ScopeChecklist
            items={scopeItems}
            selected={form.scope_items}
            onChange={(scope_items) => setForm({ ...form, scope_items })}
          />
        </section>

        <section className={`${cardClass} p-6 space-y-4`}>
          <h2 className="font-medium text-slate-900">3. Customer & location</h2>
          <SearchableSelect
            label="Account"
            value={form.account_id}
            onChange={(account_id) =>
              setForm({ ...form, account_id, service_location_id: '' })
            }
            options={accountOptions}
            placeholder={
              form.property_category
                ? 'Search accounts…'
                : 'Choose property type first'
            }
            disabled={!form.property_category}
            required
          />
          {form.property_category && filteredAccounts.length === 0 && (
            <p className="text-sm text-amber-700">
              No matching accounts for this property type. Create a{' '}
              {form.property_category === 'residential' ? 'residential' : 'business'} account
              first.
            </p>
          )}
          <SearchableSelect
            label="Location"
            value={form.service_location_id}
            onChange={(service_location_id) => setForm({ ...form, service_location_id })}
            options={locationOptions}
            placeholder={form.account_id ? 'Search locations…' : 'Select an account first'}
            disabled={!form.account_id}
            required
          />
        </section>

        <section className={`${cardClass} p-6 space-y-4`}>
          <h2 className="font-medium text-slate-900">4. Schedule & notes</h2>
          <DateTimeFields
            label="Scheduled start"
            value={form.scheduled_start}
            onChange={(scheduled_start) => setForm({ ...form, scheduled_start })}
          />
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <button type="submit" className={btnPrimary}>
            Create job
          </button>
        </section>
      </form>
    </div>
  );
};

export default CreateJob;
